import { FormField as FormFieldType } from '../../Types/forms';
import { useFormContext } from './FormContext';
import { FormFieldComponentProps } from './fields/fieldProps';
import TextField from './fields/TextField';
import TextAreaField from './fields/TextAreaField';
import CheckboxField from './fields/CheckboxField';
import CheckboxListField from './fields/CheckboxListField';
import RadioButtonListField from './fields/RadioButtonListField';
import DropdownField from './fields/DropdownField';
import SliderField from './fields/SliderField';
import LabelField from './fields/LabelField';
import RecaptchaField from './fields/RecaptchaField';
import PhoneCountryCodeField from './fields/PhoneCountryCodeField';
import VideoField from './fields/VideoField';

type FormFieldProps = {
	field: FormFieldType;
};

const FormField = ({ field }: FormFieldProps) => {
	const { values, errors, touched, hiddenFields, disabled, setValue, setTouched } = useFormContext();

	if (hiddenFields.has(field.key)) return null;

	const props: FormFieldComponentProps = {
		field,
		value: values[field.key] ?? '',
		error: errors[field.key] ?? null,
		touched: touched[field.key] ?? false,
		disabled,
		onChange: (value: string) => setValue(field.key, value),
		onBlur: () => setTouched(field.key),
	};

	const widthClass = field.widthClass ? `biz-form-field--${field.widthClass}` : '';

	return (
		<div className={`biz-form-field ${widthClass}`.trim()}>
			{renderFieldComponent(props)}
			{props.touched && props.error && <div className='biz-form-field__error'>{getErrorMessage(props.error)}</div>}
		</div>
	);
};

function renderFieldComponent(props: FormFieldComponentProps) {
	switch (props.field.typeKey) {
		case 'input':
			return <TextField {...props} />;
		case 'textarea':
			return <TextAreaField {...props} />;
		case 'checkbox':
			return <CheckboxField {...props} />;
		case 'checkboxlist':
			return <CheckboxListField {...props} />;
		case 'radiobuttonlist':
			return <RadioButtonListField {...props} />;
		case 'dropdownlist':
			return <DropdownField {...props} />;
		case 'slider':
			return <SliderField {...props} />;
		case 'label':
			return <LabelField {...props} />;
		case 'recaptcha':
			return <RecaptchaField {...props} />;
		case 'phonecountrycode':
			return <PhoneCountryCodeField {...props} />;
		case 'video':
			return <VideoField {...props} />;
		default:
			return null;
	}
}

function getErrorMessage(error: string): string {
	switch (error) {
		case 'required':
			return 'This field is required';
		case 'email':
			return 'Please enter a valid email address';
		default:
			return 'Invalid value';
	}
}

export default FormField;
